import type {
  IngestedProductSchema,
  IngestedVariantSchema,
} from "@/types/ingestedProduct";

/**
 * Sample ingested catalog products for tests, Storybook, and local MSW handlers.
 * Aligns with GET /{site_id}/products/{product_id}/ (see {@link getIngestedProduct}).
 */

export const productImageUrl =
  "https://cdn.shopify.com/s/files/1/0838/4441/files/Seine_Mid_Rise_Skinny_Jeans_27_Inch_Dark_Indigo_USPA0302_008_1329.jpg?v=1733000789";
export const variantImageUrl =
  "https://cdn.shopify.com/s/files/1/0838/4441/files/Seine_Mid_Rise_Skinny_Jeans_27_Inch_Dark_Indigo_USPA0302_008_1329.jpg?v=1733000789";

export const mockIngestedProducts = [
  {
    site_id: "r2dkZLP",
    site: "demo-store.example.com",
    vendor_product_id: "vp_7xK2mN",
    title: "Kentest Trail Runner",
    handle: "kentest-trail-runner",
    url: "https://demo-store.example.com/products/kentest-trail-runner",
    status: "active",
    description:
      "Lightweight trail shoe with responsive cushioning for mixed terrain.",
    short_description: "Trail runner with all-day comfort.",
    brand: "Kentest",
    vendor: "Kentest Athletics",
    sku: "KTR-001-BLU-9",
    identifiers: {
      upc: "012345678905",
      mpn: "KTR-001",
    },
    ai_managed_fields: ["short_description", "meta_description"],
    price: {
      currency: "USD",
      amount: "129.00",
      sale: "",
      cost: "65.00",
    },
    availability: {
      is_available: true,
      quantity: 24,
      status: "active",
    },
    assets: [
      {
        handle: "featured",
        url: productImageUrl,
        title: "",
        type: "image",
        alt: "Kentest Trail Runner, side view",
        origin: "PDP_HERO",
        width: 1200,
        height: 800,
        file_size: 245_000,
      },
    ],
    variants: [
      {
        vendor_variant_id: "vv_3pL9qR",
        title: "Blue / US 9",
        sku: "KTR-001-BLU-9",
        url: "https://demo-store.example.com/products/kentest-trail-runner?variant=vv_3pL9qR",
        primary_option: "Color",
        price: { amount: "129.00", currency: "USD", sale: "", cost: "65.00" },
        availability: {
          is_available: true,
          quantity: 8,
          status: "active",
        },
        price_before_last_drop: "149.00",
        last_price_drop_percent: 13.4,
        price_drop_active: true,
        assets: [
          {
            handle: "variant",
            url: variantImageUrl,
            title: "",
            type: "image",
            alt: "Blue colorway",
            origin: "PDP_ALT",
            width: 900,
            height: 600,
            file_size: 180_000,
          },
        ],
        options: [
          {
            name: "Color",
            value: "Blue",
            position: 0,
            is_primary: true,
          },
          {
            name: "Size",
            value: "US 9",
            position: 1,
            is_primary: false,
          },
        ],
        vendor_metadata: {},
      },
      {
        vendor_variant_id: "vv_4qM0sT",
        title: "Black / US 10",
        sku: "KTR-001-BLK-10",
        url: "https://demo-store.example.com/products/kentest-trail-runner?variant=vv_4qM0sT",
        primary_option: "Color",
        price: { amount: "129.00", currency: "USD", sale: "", cost: "65.00" },
        availability: {
          is_available: false,
          quantity: 0,
          status: "inactive",
        },
        assets: [],
        options: [
          {
            name: "Color",
            value: "Black",
            position: 0,
            is_primary: true,
          },
          {
            name: "Size",
            value: "US 10",
            position: 1,
            is_primary: false,
          },
        ],
        vendor_metadata: {},
      },
    ],
    categories: ["Footwear", "Running"],
    collections: ["Spring Launch"],
    tags: ["trail", "running", "featured"],
    volume_case_pack: "6",
    returnable: true,
    country_of_origin: "VN",
    expiration_date: null,
    boxed_length: 33,
    boxed_width: 22,
    boxed_height: 13,
    boxed_weight: 480,
    opened_length: 32,
    opened_width: 12,
    opened_height: 11,
    opened_weight: 310,
    shipping_restrictions: "none",
    warranty: "1-year limited warranty",
    meta_description:
      "Kentest Trail Runner — lightweight cushioning for mixed terrain. Free returns within 30 days.",
    meta_keywords: ["trail", "running", "footwear", "Kentest"],
    vendor_metadata: {},
    published_at: "2025-07-10T01:27:00.473115",
    ingested_at: "2026-04-12T01:27:00.473115",
    ingestion_source: "shopify",
    ingestion_version: 1,
    sku_score: {
      total: 630,
      completeness: {
        score: 78,
        breakdown: {
          buckets: [
            {
              key: "core",
              label: "Core fields",
              score: 38,
              max: 45,
              checks: [
                {
                  key: "title",
                  label: "Title",
                  passed: true,
                  weight: 8,
                  awarded: 8,
                  source: "ingested",
                },
                {
                  key: "description",
                  label: "Description (80+ chars)",
                  passed: false,
                  weight: 6,
                  awarded: 0,
                  reason: "Description is 72 chars; 80 needed",
                  source: "ingested",
                },
              ],
            },
            {
              key: "media",
              label: "Media",
              score: 18,
              max: 25,
              checks: [
                {
                  key: "variant_images",
                  label: "Every sellable variant has an image",
                  passed: false,
                  weight: 5,
                  awarded: 0,
                  reason: "1 of 2 variants has an image",
                },
              ],
            },
            {
              key: "enrichment",
              label: "Page copy",
              score: 12,
              max: 20,
              checks: [
                {
                  key: "features",
                  label: "At least one feature",
                  passed: false,
                  weight: 5,
                  awarded: 0,
                  reason: "No features generated",
                  source: "generated",
                },
              ],
            },
            {
              key: "availability",
              label: "Availability and freshness",
              score: 10,
              max: 10,
              checks: [],
            },
          ],
        },
      },
      commercial: {
        score: 64,
        breakdown: {
          inputs: {
            product_orders_30d: 18,
            product_units_30d: 22,
            cart_adds_30d: 57,
            views_30d: 1240,
            product_orders_7d: 5,
          },
          demand_value: 15.2,
          percentile: 0.64,
          site_product_count: 4,
          has_engagement_data: true,
        },
        // 57 / 1,240 = 4.6% of views against a 7% site rate.
        insight: "view_to_cart_below_site",
      },
      weights: { completeness: 0.5, commercial: 0.5 },
      site_benchmarks: {
        completeness_bucket_medians: {
          core: 30,
          media: 14,
          enrichment: 16,
          availability: 9,
        },
        view_to_cart_rate: 0.07,
        cart_to_order_rate: 0.28,
      },
      version: 2,
    },
  },
  {
    site_id: "r2dkZLP",
    site: "demo-store.example.com",
    vendor_product_id: "vp_8yL3nP",
    title: "July Promo Water Bottle",
    handle: "july-promo-water-bottle",
    url: "https://demo-store.example.com/products/july-promo-water-bottle",
    status: "active",
    description: "Insulated 24oz bottle — limited July promo run.",
    short_description: "24oz insulated bottle.",
    brand: "HydrateCo",
    vendor: "HydrateCo Wholesale",
    sku: "BOT-HYD-024-RED",
    identifiers: {
      ean: "5901234123457",
    },
    ai_managed_fields: [],
    price: {
      currency: "USD",
      amount: "24.99",
      sale: "19.99",
      cost: "8.50",
    },
    availability: {
      is_available: true,
      quantity: 200,
      status: "active",
    },
    assets: [
      {
        handle: "featured",
        url: productImageUrl,
        title: "",
        type: "image",
        alt: "Red insulated bottle",
        origin: "PDP_HERO",
        width: 800,
        height: 800,
        file_size: 120_000,
      },
    ],
    variants: [
      {
        vendor_variant_id: "vv_5rN1uV",
        title: "Red",
        sku: "BOT-HYD-024-RED",
        url: "https://demo-store.example.com/products/july-promo-water-bottle?variant=vv_5rN1uV",
        primary_option: "Color",
        price: {
          amount: "24.99",
          currency: "USD",
          sale: "19.99",
          cost: "8.50",
        },
        // Source did not report a quantity: renders as "Unknown", never 0.
        availability: {
          is_available: true,
          quantity: null,
          status: "active",
        },
        assets: [],
        options: [
          {
            name: "Color",
            value: "Red",
            position: 0,
            is_primary: true,
          },
        ],
        vendor_metadata: {},
      },
    ],
    categories: ["Accessories"],
    collections: ["July Promo"],
    tags: ["promo", "hydration"],
    volume_case_pack: "24",
    returnable: true,
    country_of_origin: "US",
    expiration_date: null,
    boxed_length: 28,
    boxed_width: 10,
    boxed_height: 10,
    boxed_weight: 420,
    opened_length: 26,
    opened_width: 9,
    opened_height: 9,
    opened_weight: 380,
    shipping_restrictions: "none",
    warranty: "90-day satisfaction guarantee",
    meta_description: "24oz insulated promo bottle — limited July run.",
    meta_keywords: ["bottle", "hydration", "promo"],
    vendor_metadata: {},
    published_at: "2025-07-15T09:00:00.000000",
    ingested_at: "2026-04-16T09:00:00.000000",
    ingestion_source: "shopify",
    ingestion_version: 2,
    // Score not computed yet (pre-backfill).
    sku_score: null,
  },
  {
    site_id: "r2dkZLP",
    site: "demo-store.example.com",
    vendor_product_id: "vp_9zM4oQ",
    title: "Archived Summer Tee",
    handle: "archived-summer-tee",
    url: "https://demo-store.example.com/products/archived-summer-tee",
    status: "inactive",
    description: "",
    short_description: "",
    brand: "",
    vendor: "Legacy Apparel Co",
    sku: "TEE-SUM-2024-WHT-M",
    identifiers: {},
    ai_managed_fields: [],
    price: {
      currency: "USD",
      amount: "0.00",
      sale: "",
      cost: "0.00",
    },
    availability: {
      is_available: false,
      quantity: 0,
      status: "discontinued",
    },
    assets: [],
    variants: [
      {
        vendor_variant_id: "vv_6sO2vW",
        title: "White / M",
        sku: "TEE-SUM-2024-WHT-M",
        url: "https://demo-store.example.com/products/archived-summer-tee?variant=vv_6sO2vW",
        primary_option: null,
        price: { amount: "0.00", currency: "USD", sale: "", cost: "0.00" },
        availability: {
          is_available: false,
          quantity: 0,
          status: "discontinued",
        },
        assets: [],
        options: [],
        vendor_metadata: {},
      },
    ],
    categories: ["Apparel"],
    collections: [],
    tags: ["archived"],
    volume_case_pack: null,
    returnable: false,
    country_of_origin: "BD",
    expiration_date: null,
    boxed_length: 0,
    boxed_width: 0,
    boxed_height: 0,
    boxed_weight: 0,
    opened_length: 0,
    opened_width: 0,
    opened_height: 0,
    opened_weight: 0,
    shipping_restrictions: "",
    warranty: "",
    meta_description: "",
    meta_keywords: [],
    vendor_metadata: {},
    published_at: "2024-06-20T14:30:00.000000",
    ingested_at: "2026-06-28T15:00:00.000000",
    ingestion_source: "shopify",
    ingestion_version: 1,
  },
  {
    site_id: "r2dkZLP",
    site: "demo-store.example.com",
    vendor_product_id: "4513277149272",
    title: "Stemless Glass Set (2)",
    handle: "stemless-glass-set-2",
    url: "https://demo-store.example.com/products/stemless-glass-set-2",
    status: "active",
    description: "Double-walled stemless wine glasses in a gift box.",
    short_description: "2-pack stemless glass set.",
    hero_preheader: "Gift-ready drinkware",
    hero_copy: "Double-walled glasses that keep drinks cold and hands dry.",
    body_headline: "Designed for everyday elegance",
    feature_headline: "Why you'll love them",
    features: [
      {
        rank: 1,
        attribute: "Double-walled insulation",
        subheadline: "No condensation",
        description: "Keeps drinks at temperature without a wet ring.",
        scene_images: [],
      },
    ],
    benefit_headline: "The perfect gift",
    brand: "Stemless & Co",
    vendor: "Demo Store",
    sku: "SGL-2PK",
    identifiers: { mpn: "GL-4513277149272" },
    ai_managed_fields: [],
    price: {
      currency: "USD",
      amount: "32.00",
      sale: "",
      cost: "12.00",
    },
    availability: {
      is_available: true,
      quantity: 100,
      status: "active",
    },
    assets: [
      {
        handle: "featured",
        url: productImageUrl,
        title: "",
        type: "image",
        alt: "Stemless glass set",
        origin: "PDP_HERO",
        width: 1200,
        height: 800,
        file_size: 200_000,
      },
    ],
    variants: [
      {
        vendor_variant_id: "41554530074712",
        title: "12oz — Ice blue",
        sku: "SGL-12OZ-IB",
        url: "https://demo-store.example.com/products/stemless-glass-set-2?variant=41554530074712",
        primary_option: "Color",
        price: { amount: "32.00", currency: "USD", sale: "", cost: "12.00" },
        availability: {
          is_available: true,
          quantity: 50,
          status: "active",
        },
        assets: [],
        options: [
          {
            name: "Color",
            value: "Ice blue",
            position: 0,
            is_primary: true,
          },
        ],
        vendor_metadata: {},
      },
      {
        vendor_variant_id: "43535084486744",
        title: "12oz — Clear",
        sku: "SGL-12OZ-CL",
        url: "https://demo-store.example.com/products/stemless-glass-set-2?variant=43535084486744",
        primary_option: "Color",
        price: { amount: "32.00", currency: "USD", sale: "", cost: "12.00" },
        availability: {
          is_available: true,
          quantity: 50,
          status: "active",
        },
        assets: [],
        options: [
          {
            name: "Color",
            value: "Clear",
            position: 0,
            is_primary: true,
          },
        ],
        vendor_metadata: {},
      },
    ],
    categories: ["Drinkware"],
    collections: ["Giftable"],
    tags: ["glass", "wine", "double-wall"],
    volume_case_pack: "4",
    returnable: true,
    country_of_origin: "CN",
    expiration_date: null,
    boxed_length: 20,
    boxed_width: 15,
    boxed_height: 8,
    boxed_weight: 400,
    opened_length: 19,
    opened_width: 14,
    opened_height: 7,
    opened_weight: 350,
    shipping_restrictions: "none",
    warranty: "30-day satisfaction",
    meta_description: "Double-walled stemless wine glasses — 2 pack.",
    meta_keywords: ["stemless", "wine", "glasses"],
    characteristics: [
      { field: "color", values: ["clear"] },
      { field: "material", values: ["borosilicate glass"] },
      { field: "key_features", values: ["double-walled", "dishwasher-safe"] },
      { field: "case_pack", values: [2] },
    ],
    vendor_metadata: {},
    published_at: "2025-10-10T10:00:00.000000",
    ingested_at: "2026-04-20T10:00:00.000000",
    ingestion_source: "shopify",
    ingestion_version: 1,
    updated_at: "2026-04-20T10:05:00.000000",
    vendor_updated_at: "2026-04-19T22:00:00.000000",
    sku_score: {
      total: 408,
      completeness: {
        score: 92,
        breakdown: {
          buckets: [
            {
              key: "core",
              label: "Core fields",
              score: 45,
              max: 45,
              checks: [
                {
                  key: "title",
                  label: "Title",
                  passed: true,
                  weight: 8,
                  awarded: 8,
                  source: "ingested",
                },
                {
                  key: "description",
                  label: "Description (80+ chars)",
                  passed: true,
                  weight: 6,
                  awarded: 6,
                  source: "override",
                },
              ],
            },
            {
              key: "media",
              label: "Media",
              score: 19,
              max: 25,
              checks: [
                {
                  key: "alt_text",
                  label: "Images have alt text",
                  passed: false,
                  weight: 4,
                  awarded: 0,
                  reason: "1 image is missing alt text",
                },
              ],
            },
            {
              key: "enrichment",
              label: "Page copy",
              score: 20,
              max: 20,
              checks: [],
            },
            {
              key: "availability",
              label: "Availability and freshness",
              score: 8,
              max: 10,
              checks: [
                {
                  key: "vendor_freshness",
                  label: "Vendor updated within 30 days",
                  passed: false,
                  weight: 2,
                  awarded: 0,
                  reason: "Last vendor update 154 days ago",
                },
              ],
            },
          ],
        },
      },
      // Site has no Omni delivery for this product yet.
      commercial: {
        score: 0,
        breakdown: {
          inputs: {},
          demand_value: 0,
          percentile: null,
          site_product_count: 4,
          has_engagement_data: false,
        },
      },
      weights: { completeness: 0.5, commercial: 0.5 },
      version: 2,
    },
    custom_fields: [
      {
        id: 1301,
        external_id: "21881298681944",
        name: "global:title_tag",
        value: "Double Walled Stemless Wine Glasses: 2-Pack",
        source: "shopify",
        type: "string",
        description: null,
      },
    ],
    benefit_statements: [
      {
        benefit_id: "bft_04",
        rank: 1,
        headline: "Superior Double-Walled Construction",
        body: "Experience the benefits of double-walled glass construction, offering enhanced insulation and a comfortable hold without condensation.",
        category: "materials_ingredients",
        grounding_quotes: ["double-walled glass"],
        scene_images: [
          {
            vendor_variant_id: "41554530074712",
            source_asset_id: "12oz---ice-blue",
            output_artifact_uri:
              "gs://queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-04--41554530074712:1/output.png",
            output_http_url:
              "https://storage.googleapis.com/queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-04--41554530074712:1/output.png",
            status: "succeeded",
          },
          {
            vendor_variant_id: "43535084486744",
            source_asset_id: "12oz---clear",
            output_artifact_uri:
              "gs://queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-04--43535084486744:1/output.png",
            output_http_url:
              "https://storage.googleapis.com/queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-04--43535084486744:1/output.png",
            status: "succeeded",
          },
        ],
      },
      {
        benefit_id: "bft_02",
        rank: 2,
        headline: "Comfortable and Resilient Design",
        body: "Enjoy a premium drinking experience with glasses that are lightweight yet durable, perfect for everyday enjoyment.",
        category: "comfort_fit",
        grounding_quotes: ["lightweight yet durable"],
        scene_images: [
          {
            vendor_variant_id: "41554530074712",
            source_asset_id: "12oz---ice-blue",
            output_artifact_uri:
              "gs://queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-02--41554530074712:1/output.png",
            output_http_url:
              "https://storage.googleapis.com/queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-02--41554530074712:1/output.png",
            status: "succeeded",
          },
          {
            vendor_variant_id: "43535084486744",
            source_asset_id: "12oz---clear",
            output_artifact_uri:
              "gs://queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-02--43535084486744:1/output.png",
            output_http_url:
              "https://storage.googleapis.com/queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-02--43535084486744:1/output.png",
            status: "succeeded",
          },
        ],
      },
      {
        benefit_id: "bft_03",
        rank: 3,
        headline: "Ready to Gift",
        body: "This Stemless Glass Set comes in a beautiful gift box, making it a thoughtful and elegant present for any occasion.",
        category: "value",
        grounding_quotes: [
          "Stemless Glass Set (2)",
          "It comes in a beautiful gift box",
        ],
        scene_images: [
          {
            vendor_variant_id: "41554530074712",
            source_asset_id: "12oz---ice-blue",
            output_artifact_uri:
              "gs://queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-03--41554530074712:1/output.png",
            output_http_url:
              "https://storage.googleapis.com/queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-03--41554530074712:1/output.png",
            status: "succeeded",
          },
          {
            vendor_variant_id: "43535084486744",
            source_asset_id: "12oz---clear",
            output_artifact_uri:
              "gs://queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-03--43535084486744:1/output.png",
            output_http_url:
              "https://storage.googleapis.com/queen-one-staging-skubert-artifacts/jobs/4513277149272-919089efd49d/generate_scene_images/generate_scene_images:bft-03--43535084486744:1/output.png",
            status: "succeeded",
          },
        ],
      },
    ],
  },
] as const satisfies readonly IngestedProductSchema[];

/**
 * Default single-product fixture: stemless glass PDP sample with `custom_fields` and
 * `benefit_statements` (aligns with `includes=custom_fields,benefit_statements`). Other
 * list entries are matched by {@link resolveMockIngestedProduct}.
 */
export const mockIngestedProduct: IngestedProductSchema =
  mockIngestedProducts[3];

/** Finds a variant by `vendor_variant_id` across fixtures, else the default product's first. */
export function resolveMockIngestedVariant(
  variantId: string,
): IngestedVariantSchema {
  const decodedId = decodeURIComponent(variantId);
  for (const product of mockIngestedProducts) {
    const variant = product.variants.find(
      (v) => v.vendor_variant_id === decodedId,
    );
    if (variant) {
      return JSON.parse(
        JSON.stringify({
          ...variant,
          vendor_product_id: product.vendor_product_id,
        }),
      ) as IngestedVariantSchema;
    }
  }
  const [fallback] = mockIngestedProduct.variants;
  return JSON.parse(
    JSON.stringify({
      ...fallback,
      vendor_product_id: mockIngestedProduct.vendor_product_id,
    }),
  ) as IngestedVariantSchema;
}

/**
 * Picks a fixture by `vendor_product_id`, `sku`, or `handle`, else {@link mockIngestedProduct}.
 * Sets `site_id` from the request. Deep-cloned for React Query (same idea as dragon-tiles mocks).
 */
export function resolveMockIngestedProduct(
  siteId: string,
  productId: string,
): IngestedProductSchema {
  const decodedId = decodeURIComponent(productId);
  const match = mockIngestedProducts.find(
    (p) =>
      p.vendor_product_id === decodedId ||
      p.sku === decodedId ||
      p.handle === decodedId,
  );
  const base = match ?? mockIngestedProduct;
  const resolved: IngestedProductSchema = {
    ...base,
    site_id: siteId,
  };
  // Deep clone for React Query (matches dragon-tiles mock pattern).
  return JSON.parse(JSON.stringify(resolved)) as IngestedProductSchema;
}
